"use client";

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Link, Mail, Share2, X } from "lucide-react";
import BrandIcon, {
  type BrandName,
} from "@/src/shared/components/icons/BrandIcon";
import { type SharePlatform, useShare } from "@/src/shared/hooks/useShare";
import ShareOutlineIcon from "@/src/shared/components/icons/ShareOutlineIcon";

interface IShareButtonProps {
  url?: string;
  title?: string;
  text?: string;
  className?: string;
  /**
   * `hero`: the app's white "Share" pill over a listing's photos (mobile). `text`: Airbnb's
   * details-page link: the share mark and an underlined "Share", no fill.
   */
  variant?: "default" | "minimal" | "compact" | "white" | "hero" | "text";
  onShare?: (method: string) => void;
  onSave?: (saved: boolean) => void;
  saved?: boolean;
}

/** A brand mark as an icon component, so it sits in the list beside lucide icons. */
const brand = (name: BrandName) => {
  const BrandMark = ({ className }: { className?: string }) => (
    <BrandIcon name={name} className={className} />
  );
  BrandMark.displayName = `BrandMark(${name})`;
  return BrandMark;
};

const ShareButton: React.FC<IShareButtonProps> = ({
  url,
  title = "Check this out!",
  text = "I found this amazing place you should check out.",
  className = "",
  variant = "default",
  onShare,
}) => {
  const share = useShare({
    url,
    title,
    text,
    autoCloseDelayMs: 1500,
  });

  const getButtonClasses = () => {
    if (variant === "hero") {
      return "inline-flex h-9 items-center gap-2 rounded-full bg-white pl-3 pr-4 text-sm font-medium text-ink shadow-sm";
    }
    if (variant === "text") {
      return "inline-flex h-[34px] items-center gap-2 rounded-lg p-2 text-14 font-medium leading-[18px] text-ink underline transition-colors hover:bg-[#F7F7F7]";
    }
    const base =
      "flex items-center space-x-1 px-3 py-2 text-sm font-medium transition-all duration-200 rounded-lg";
    switch (variant) {
      case "minimal":
        return `${base} inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border-divider-100 bg-coral-pink-50 text-coral-pink-500`;
      case "compact":
        return `${base} inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-coral-pink-50 text-coral-pink-500`;
      case "white":
        return `${base} inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border border-gray-200 bg-white text-black hover:bg-gray-50`;
      default:
        return `${base} inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border-divider-100 bg-coral-pink-50 text-coral-pink-500`;
    }
  };

  const socials: Array<{
    key: SharePlatform;
    label: string;
    Icon: React.ComponentType<{ className?: string }>;
    hover: string;
  }> = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      Icon: brand("whatsapp"),
      hover: "hover:bg-green-50",
    },
    {
      key: "facebook",
      label: "Facebook",
      Icon: brand("facebook"),
      hover: "hover:bg-blue-50",
    },
    {
      key: "twitter",
      label: "Twitter",
      Icon: brand("twitter"),
      hover: "hover:bg-blue-50",
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      Icon: brand("linkedin"),
      hover: "hover:bg-blue-50",
    },
    {
      key: "telegram",
      label: "Telegram",
      Icon: brand("telegram"),
      hover: "hover:bg-blue-50",
    },
    { key: "email", label: "Email", Icon: Mail, hover: "hover:bg-gray-50" },
  ];

  return (
    <div className={`relative flex items-center space-x-2 ${className}`}>
      {/* Share */}
      <div className="relative">
        <motion.button
          onClick={async () => {
            await share.share();
            onShare?.(share.isNativeSupported ? "native" : "dropdown");
          }}
          className={getButtonClasses()}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          aria-haspopup="menu"
          aria-expanded={share.isOpen}
          type="button"
        >
          {variant === "hero" ? (
            <img
              src="/icons/listing/listing_hero_share.svg"
              alt=""
              className="h-[15px] w-[18px]"
            />
          ) : variant === "text" ? (
            <ShareOutlineIcon />
          ) : (
            <Share2 className="w-4 h-4" />
          )}
          <span>Share</span>
        </motion.button>

        {/* Dropdown */}
        <AnimatePresence>
          {share.isOpen && (
            <>
              <motion.div
                className="fixed inset-0 z-10"
                onClick={share.close}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="absolute top-full mt-2 right-0 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20"
                role="menu"
                aria-label="Share menu"
              >
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
                  <span className="font-medium text-ink-black">
                    Share this listing
                  </span>
                  <button
                    onClick={share.close}
                    className="text-ink-muted hover:text-ink-secondary"
                    type="button"
                    aria-label="Close share menu"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Copy */}
                <button
                  onClick={async () => {
                    const ok = await share.copy();
                    onShare?.(ok ? "clipboard" : "clipboard-fail");
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                  type="button"
                >
                  {share.isCopied ? (
                    <Check className="w-5 h-5 text-success-verified" />
                  ) : (
                    <Link className="w-5 h-5 text-ink-secondary" />
                  )}
                  <div className="text-left">
                    <div className="font-medium text-ink-black">
                      {share.isCopied ? "Link copied!" : "Copy link"}
                    </div>
                    <div className="text-sm text-ink-gray">
                      {share.isCopied
                        ? "Link copied to clipboard"
                        : "Get a link you can share anywhere"}
                    </div>
                  </div>
                </button>

                {/* Socials */}
                <div className="border-t border-gray-100 pt-2">
                  <div className="grid grid-cols-2 gap-1 px-2 pb-2">
                    {socials.map(({ key, label, Icon, hover }) => (
                      <button
                        key={key}
                        onClick={() => {
                          share.shareTo(key);
                          onShare?.(key);
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${hover}`}
                        type="button"
                      >
                        <Icon className="w-5 h-5 text-ink-dark" />
                        <span className="text-sm font-medium text-ink-dark">
                          {label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ShareButton;
